import Config from "./Config"
import Util from "./Util"

export default class Log
{
	static IsLogging = false;

	static ConsoleLog;
	static ConsoleInfo;
	static ConsoleDebug;
	static ConsoleWarn;
	static ConsoleError;

	static ConsoleTable;

	static ConsoleTime;
	static ConsoleTimeEnd;

	static ConsoleGroup;
	static ConsoleGroupCollapsed;
	static ConsoleGroupEnd;

	/**
     * [Note]
     *
	 * @returns void
     **/
	static boot()
	{
	    let basicMapping = {
			ConsoleInfo: 'info',
			ConsoleDebug: 'debug',
			ConsoleWarn: 'warn',
			ConsoleError: 'error',
			ConsoleTime: 'time',
			ConsoleTimeEnd: 'timeEnd',
			ConsoleGroupCollapsed: 'groupCollapsed',
			ConsoleGroupEnd: 'groupEnd',
		};

        window.console.red = (text, ...params) => {
            return console.log(`%c${text}`, "color:red", ...params);
        }
        window.console.green = (text, ...params) => {
            return console.log(`%c${text}`, "color:green", ...params);
        }
        window.console.blue = (text, ...params) => {
            return console.log(`%c${text}`, "color:blue", ...params);
        }
        window.console.yellow = (text, ...params) => {
            return console.log(`%c${text}`, "color:yellow", ...params);
        }

        if (Function.prototype.bind) {
            window.log = Function.prototype.bind.call(console.log, console);
        }
        else {
            window.log = function() {
                Function.prototype.apply.call(console.log, console, arguments);
            };
        }
		for (let [prop, method] of Object.entries(basicMapping)) {
			this[prop] = Function.prototype.bind.call(console[method], console);
		}

		let extendedMapping = {
			ConsoleLog: ['log', 'line'],
			ConsoleGroup: ['group', 'groupStart'],
			ConsoleTable: ['table', 'basicTable'],
		};

		for (let [prop, methods] of Object.entries(extendedMapping)) {
			let sourceMethod = methods[0];
			let newMethod = methods[1];
			this[prop] = Function.prototype.bind.call(console[sourceMethod], console);
			// console[sourceMethod] = function(...input) {
			//     Log[newMethod](...input);
			// };
		}
	}

	/**
     * [Note]
     *
	 * @returns {Object}
     **/
	static config()
	{
		return Config.get('log');
	}

	/**
     * [Note]
     *
	 * @returns {Boolean}
     **/
	static isDisabled()
	{
		let config = this.config()

		return config && config.hide;
	}

    /**
     * [Note]
     *
     * @param {Array[mixed]} input
	 * @returns void
     **/
	static line(...input)
	{
		this.run(() => {
			this.ConsoleLog(...input);
		});
	}

	/**
     * [Note]
     *
	 * @param {String} message
	 * @param {Object} styles
	 * @returns void
	**/
	static styled(message, styles)
	{
		this.run(() => {
			var stylesString = "";
			Util.forEach(styles, (value, property) => {
				stylesString += `${property}:${value};`;
			});

			this.ConsoleLog(`%c${message}`, stylesString);
		});
	}

    /**
     * Highlighted blue to easily distinguish from normal logs
     * Good for temporary testing
     *
     * @param {Array[mixed]} input
	 * @returns void
     **/
	static debug(...input)
	{
		this.run(() => {
			this.ConsoleDebug(...input);
		});
	}

    /**
     * Styled Info Message
     *
     * @param {Array[mixed]} input
	 * @returns void
     **/
	static info(...input)
	{
		this.run(() => {
			this.ConsoleInfo(...input);
		});
	}

    /**
     * Styled Warning Message
     *
     * @param {Array[mixed]} input
	 * @returns void
     **/
	static warn(...input)
	{
		this.run(() => {
			this.ConsoleWarn(...input);
		});
	}

    /**
     * Styled Error Message
     *
     * @param {Array[mixed]} input
	 * @returns void
     **/
	static error(...input)
	{
		this.run(() => {
			this.ConsoleError(...input);
		});
	}

    /**
     * [Note]
     *
     * @param {Array[mixed]} input
	 * @returns void
     **/
	static basicTable(...input)
	{
		this.run(() => {
			this.ConsoleTable(...input);
		});
	}

    /**
     * [Note]
     *
     * @param {Array[String]} headers
     * @param {Array[mixed]} rows
	 * @returns void
     **/
	static table(headers, rows = [])
	{
		this.run(() => {
			if ( Util.isEmpty(rows) ) {
				return this.ConsoleTable(headers);
			}

			if ( Util.isArray(Util.head(rows)) ) {
				rows = Util.map(rows, row => {
					var object = {};
					Util.forEach(row, (property, index) => {
						object[headers[index]] = property;
					});

					return object;
				});
			}

			this.ConsoleTable(rows.all(), headers);
		});
	}

    /**
     * Track execution time
     *
     * @param {String} event
     * @param {Function} callback
	 * @returns void
     **/
	static clock(event, callback)
	{
		this.run(() => {
			this.ConsoleTime(event);
			callback();
			this.ConsoleTimeEnd(event);
		});
	}

    /**
     * Start execution time
     *
     * @param {Array[mixed]} input
	 * @returns void
     **/
	static time(...input)
	{
		this.run(() => {
			this.ConsoleTime(...input);
		});
	}

    /**
     * End execution time
     *
     * @param {Array[mixed]} input
	 * @returns void
     **/
	static timeEnd(...input)
	{
		this.run(() => {
			this.ConsoleTimeEnd(...input);
		});
	}

	/**
     * [Note]
     *
     * @param {String} append
	 * @returns {String}
     **/
	static prefixWithTime(append)
	{
		return new Date().toLocaleTimeString() + ' - ' + append;
	}

    /**
     * [Note]
     *
     * @param {String} title
     * @param {Array[String]} lines
     * @param {Boolean} collapsed
	 * @returns void
     **/
	static group(title, lines, collapsed = true)
	{
		this.run(() => {
            this.unvalidatedGroup(title, lines, collapsed);
	    });
	}

    /**
     * [Note]
     *
     * @param {Array[mixed]} input
	 * @returns void
     **/
	static groupStart(...input)
	{
		this.run(() => {
            this.ConsoleGroup(...input);
	    });
	}

    /**
     * [Note]
     *
     * @param {Array[mixed]} input
	 * @returns void
     **/
	static groupCollapsed(...input)
	{
		this.run(() => {
            this.ConsoleGroupCollapsed(...input);
	    });
	}

    /**
     * [Note]
     *
     * @param {Array[mixed]} input
	 * @returns void
     **/
	static groupEnd(...input)
	{
		this.run(() => {
            this.ConsoleGroupEnd(...input);
	    });
	}

    /**
     * [Note]
     *
     * @param {String} title
     * @param {Array[String]} lines
     * @param {Boolean} collapsed
	 * @returns void
     **/
	static unvalidatedGroup(title, lines, collapsed = true)
	{
        try {
        	let method = collapsed
        		? 'ConsoleGroupCollapsed'
        		: 'ConsoleGroup';

            this[method](
            	Log.prefixWithTime(title)
            );

            Util.forEach(lines, line => {
                if ( Util.isString(line) ) {
                    this.ConsoleLog(line);
                }
                else {
                    var method = Util.head(
                        Util.keys(line)
                    );
                    if (this[method] !== undefined) {
                        this[method](...line[method]);
                    }
                    else {
                        this.ConsoleLog(line);
                    }
                }
            });

            this.ConsoleGroupEnd();
		} catch (e) {
        	this.ConsoleLog(title, lines);
		}
	}

    /**
     * [Note]
     *
     * @param {String} nickname
     * @param {Object} object
     * @param {Boolean} collapsed
	 * @returns void
     **/
	static object(nickname, object, collapsed = true)
	{
		this.run(() => {
			let lines = [];
			Util.forEach(object, (value, property) => {
				if (value instanceof Date || ! Util.isObject(value) ) {
					lines.push(property + ': ' + value);
				}
				else {
					lines.push(
						Log.loggableObject(property, value)
					);
				}
			});

			this.unvalidatedGroup(nickname, lines, collapsed);
		});
	};

	/**
     * [Note]
     *
	 * @param {String} logKey
	 * @param {Object} params
	 * @returns {Object}
	**/
	static loggableObject(logKey, params)
	{
		let paramsCopy;
		try {
			paramsCopy = {...params};
		} catch (e) {
			paramsCopy = params;
		}

		return {'unvalidatedGroup' : [
			logKey + ': ',
			Util.map(paramsCopy, (value, key) => {
				if (Util.isObject(value)) {
					return Log.loggableObject(key, value);
				}

				return key + ': ' + value;
			})
		]};
	}

	/**
     * [Note]
     *
	 * @param {Function} callback
	 * @returns void
	**/
	static async run(callback)
	{
		if (Log.isDisabled()) {
			return;
		}

		while (Log.IsLogging) {
			await Util.wait(.5);
		}

		Log.IsLogging = true;

		try {
			callback();
		} catch (e) {
			this.ConsoleWarn(e);
		}

		Log.IsLogging = false;
	}
}
